import { LinkifiedText } from '../../../LinkifiedText';
import { mentionSegments } from '../../../../lib/group-maestro';

// A contact picked via "+ Contact" rides the outgoing message text as a `[Name]` token (see
// `lib/contact-references.ts` — the harness resolves it against `references.contacts`, so the WIRE
// text must keep the bracket form). In the sender's own bubble we render that token as a bold
// `@Name` mention — the same idiom the composer chip and the native `@ContactName` mention use —
// while everything around it keeps normal URL linkification.
//
// Deliberately narrow: a `[label](url)` markdown link target is left alone (mirrors the native
// guard in `ChatText.swift`), so a pasted link never renders as a stray mention.
const CONTACT_TOKEN = /\[([^[\]\n]+)\]/g;

const MENTION_STYLE = { fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 2 } as const;

/**
 * `mentionNames` (a Symphony group's `groupMentionNames`): a typed `@Name` of a member or a Maestro is drawn as the same
 * bold mention, on the sender's screen and every reader's.
 */
export function ContactMentionText({ text, mentionNames }: { text: string; mentionNames?: string[] }) {
  if (mentionNames?.length && text.includes('@')) {
    const segments = mentionSegments(text, mentionNames);
    if (segments.some((s) => s.mention)) {
      return (
        <>
          {segments.map((s, i) =>
            s.mention ? (
              <strong key={i} style={MENTION_STYLE} data-testid="group-mention">
                {s.text}
              </strong>
            ) : (
              <ContactMentionText key={i} text={s.text} />
            ),
          )}
        </>
      );
    }
  }
  const parts: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  CONTACT_TOKEN.lastIndex = 0;
  while ((match = CONTACT_TOKEN.exec(text)) !== null) {
    const after = text[match.index + match[0].length];
    // A markdown link `[label](url)` — not a contact token. Leave it to the linkifier.
    if (after === '(') continue;
    if (match.index > last) {
      parts.push(<LinkifiedText key={key++} text={text.slice(last, match.index)} />);
    }
    parts.push(
      <strong key={key++} style={MENTION_STYLE}>
        @{match[1]}
      </strong>,
    );
    last = match.index + match[0].length;
  }
  if (parts.length === 0) return <LinkifiedText text={text} />;
  if (last < text.length) parts.push(<LinkifiedText key={key++} text={text.slice(last)} />);
  return <>{parts}</>;
}
